/* Copyright (c) 2020-present CloudIO Inc */

import React, { memo, useCallback } from 'react';
import { AnimatePresence } from 'framer-motion';
import { makeStyles } from '@mui/styles';
import { useStateXAtomSetter, useStateXAtomValue } from '@cloudio-saas/statex';
import zIndex from 'constants/ZIndex';
import NotificationItem, {
  notificationAtom,
  ClearAll,
} from './NotificationItem';
import clsx from 'clsx';
import { notificationLocationAtom } from 'components/TextField/BaseField';
import { Platform } from 'componentregistry';
import { useAppID } from 'designer/atoms';
import useUser from 'root/useUser';
import { notificationLocationUserSettingsAtom } from 'utils/state';

let counter = 0;

function genId() {
  return `n-${++counter}`;
}

const useStyles = makeStyles(() => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    zIndex: zIndex.Notification,
  },
  ul: {
    position: 'fixed',
    margin: 0,
    padding: 0,
    display: 'flex',
    flexDirection: 'column',
    listStyle: 'none',
    justifyContent: 'flex-end',
    zIndex: zIndex.Notification,
    '&.top': {
      right: '50%',
      transform: 'translate(50%, 0)',
      top: 40,
    },
    '&.bottom': {
      right: '50%',
      transform: 'translate(50%, 0)',
      bottom: 40,
    },
    '&.top-right': {
      right: 20,
      top: 40,
    },
    '&.bottom-right': {
      bottom: 40,
      right: 20,
    },
  },
}));

const Notification = memo(({ platform }: { platform: Platform }) => {
  const appUid = useAppID();
  const { userName } = useUser();
  const position = useStateXAtomValue(notificationLocationAtom, {
    params: { appUid },
  });
  const userPosition = useStateXAtomValue(
    notificationLocationUserSettingsAtom,
    {
      params: { userName },
    },
  );
  const classes = useStyles();
  const notifications = useStateXAtomValue(notificationAtom);

  if (notifications.length === 0) {
    return null;
  }

  return (
    <div className={classes.root} data-metadata="cloudio">
      <ul className={clsx(classes.ul, userPosition || position)}>
        <AnimatePresence initial={false}>
          {notifications.map((item) => (
            <NotificationItem item={item} key={item.id} />
          ))}
        </AnimatePresence>
      </ul>
      {notifications.length > 1 ? (
        <ClearAll count={notifications.length} />
      ) : null}
    </div>
  );
});

function useNotification() {
  const setNotifications = useStateXAtomSetter(notificationAtom);
  const showError = useCallback(
    (msg: string) => {
      setNotifications((n) => [
        ...n,
        { id: genId(), msg, type: 'error', duplicateIds: [] },
      ]);
    },
    [setNotifications],
  );
  const showSuccess = useCallback(
    (msg: string) => {
      setNotifications((n) => [
        ...n,
        { id: genId(), msg, type: 'success', duplicateIds: [] },
      ]);
    },
    [setNotifications],
  );
  const showWarning = useCallback(
    (msg: string) => {
      setNotifications((n) => [
        ...n,
        { id: genId(), msg, type: 'warning', duplicateIds: [] },
      ]);
    },
    [setNotifications],
  );
  const showInfo = useCallback(
    (msg: string) => {
      setNotifications((n) => [
        ...n,
        { id: genId(), msg, type: 'info', duplicateIds: [] },
      ]);
    },
    [setNotifications],
  );
  return { showError, showSuccess, showWarning, showInfo };
}

export { Notification, useNotification };
